import type { TFunction } from 'i18next';

import type { AlarmState } from '../../../../utilities/customHooks/useAlarmTone';
import type { AlertPhase } from '../../hooks/logic/useInAppAlerts';
import type { StartedBy } from '../../types/domain.types';

export const takenText = (t: TFunction, by: StartedBy | null): string =>
  by === null
    ? t('closeTheLoop.inApp.takenBySomeone', 'Someone else started this')
    : t('closeTheLoop.inApp.takenBy', '{{name}} started this', { name: by.name });

export const startFailedText = (t: TFunction): string => t('closeTheLoop.inApp.startFailed', 'Couldn’t start it. Try again.');

export const moreEmergenciesText = (t: TFunction, count: number): string =>
  count === 1
    ? t('closeTheLoop.inApp.moreEmergencyOne', '+1 more emergency')
    : t('closeTheLoop.inApp.moreEmergencies', '+{{count}} more emergencies', { count });

export const moreAlertsText = (t: TFunction, count: number): string =>
  t('closeTheLoop.inApp.moreAlerts', '+{{count}} more alerts', { count });

const takenAlarmText = (t: TFunction, by: StartedBy | null): string =>
  by === null
    ? t('closeTheLoop.inApp.takenAlarmSomeone', 'Someone else started this · alarm silenced')
    : t('closeTheLoop.inApp.takenAlarm', '{{name}} started this · alarm silenced', { name: by.name });

export interface AlarmStatusFacts {
  readonly phase: AlertPhase;
  readonly hooter: string | null;
  readonly offline: boolean;
  readonly alarm: AlarmState;
}

const tapToSoundText = (t: TFunction): string => t('closeTheLoop.inApp.alarmBlocked', 'Tap or press any key to sound the alarm');

export const alarmStatusLines = (t: TFunction, { phase, hooter, offline, alarm }: AlarmStatusFacts): readonly string[] => {
  if (phase.kind === 'taken') return [takenAlarmText(t, phase.by)];
  const lines: string[] = [];
  if (hooter !== null) lines.push(hooter);
  if (offline) lines.push(t('closeTheLoop.inApp.waitingForConnection', 'Waiting for connection'));
  if (alarm === 'blocked') lines.push(tapToSoundText(t));
  if (alarm === 'muted') lines.push(t('closeTheLoop.inApp.alarmMuted', 'Alarm muted on this device'));
  return lines;
};
